import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Drawer,
  Typography,
  List,
  ListItem,
  IconButton,
  Chip,
  Divider,
  TextField,
  InputAdornment,
  Pagination,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Menu,
  MenuItem,
  Stack,
} from "@mui/material";
import {
  FormatListBulleted as FormatListBulletedIcon,
  TextFields as TextFieldsIcon,
} from "@mui/icons-material";
import {
  History as HistoryIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Restore as RestoreIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  TextFields as TextIcon,
} from "@mui/icons-material";
import { 
  getSummaryHistory, 
  deleteSummaryFromHistory, 
  updateSummaryInHistory 
} from "../../../services/summariserService";

const SummaryHistory = ({ open, onClose, onRestoreSummary, userId, machineId }) => {
  const [summaries, setSummaries] = useState([]);
  const [filteredSummaries, setFilteredSummaries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [anchorEl, setAnchorEl] = useState(null);
  const [contextMenuSummary, setContextMenuSummary] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editText, setEditText] = useState("");
  const [editId, setEditId] = useState(null);

  const itemsPerPage = 10;

  useEffect(() => {
    if (open) {
      fetchHistory();
    }
  }, [open, userId, machineId]);

  useEffect(() => {
    if (searchTerm) {
      const filtered = summaries.filter(
        (summary) =>
          summary.originalText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          summary.outputText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          summary.type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredSummaries(filtered);
    } else {
      setFilteredSummaries(summaries);
    }
    setPage(1);
  }, [searchTerm, summaries]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const response = await getSummaryHistory(userId, machineId);
      // Handle different response structures
      let historyData = response.data?.data || response.data;
      if (historyData && historyData.data) {
        historyData = historyData.data;
      }
      if (Array.isArray(historyData)) {
        setSummaries(historyData);
        setFilteredSummaries(historyData);
      } else if (historyData?.success && Array.isArray(historyData.data)) {
        setSummaries(historyData.data);
        setFilteredSummaries(historyData.data);
      }
    } catch (error) {
      showSnackbar("Failed to load history", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = (summary) => {
    onRestoreSummary(summary.outputText);
    showSnackbar("Summary restored successfully", "success");
    onClose();
  };

  const handleDelete = async () => {
    if (!selectedSummary) return;
    
    try {
      await deleteSummaryFromHistory(selectedSummary._id);
      showSnackbar("Summary deleted successfully", "success");
      fetchHistory();
      setDeleteDialogOpen(false);
      setSelectedSummary(null);
    } catch (error) {
      showSnackbar("Failed to delete summary", "error");
    }
  };

  const handleEdit = (summary) => {
    setEditId(summary._id);
    setEditText(summary.outputText);
    setEditDialogOpen(true);
    setAnchorEl(null);
  };

  const handleSaveEdit = async () => {
    if (!editId || !editText.trim()) return;
    
    try {
      await updateSummaryInHistory(editId, editText);
      showSnackbar("Summary updated successfully", "success");
      fetchHistory();
      setEditDialogOpen(false);
      setEditText("");
      setEditId(null);
    } catch (error) {
      showSnackbar("Failed to update summary", "error");
    }
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({ open: true, message, severity });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  const handleContextMenu = (event, summary) => {
    event.preventDefault();
    setAnchorEl(event.currentTarget);
    setContextMenuSummary(summary);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setContextMenuSummary(null);
  };

  const totalPages = Math.ceil(filteredSummaries.length / itemsPerPage);
  const paginatedSummaries = filteredSummaries.slice(
    (page - 1) * itemsPerPage,
    page * itemsPerPage
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: { xs: "100%", sm: 500, md: 600 },
            bgcolor: "background.paper",
            borderRadius: { sm: "16px 0 0 16px" },
          },
        }}
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
          {/* Header */}
          <Box
            sx={{
              p: 2,
              borderBottom: 1,
              borderColor: "divider",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              bgcolor: "primary.main",
              color: "white",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <HistoryIcon />
              <Typography variant="h6">Summary History</Typography>
              <Chip
                label={`${summaries.length} items`}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,0.2)", color: "white" }}
              />
            </Box>
            <IconButton onClick={onClose} sx={{ color: "white" }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Search Bar */}
          <Box sx={{ p: 2 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by content or type..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm("")}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* List of Summaries */}
          <Box sx={{ flex: 1, overflow: "auto", px: 2 }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : paginatedSummaries.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 4 }}>
                <HistoryIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography color="text.secondary">No saved summaries yet</Typography>
                <Typography variant="body2" color="text.secondary">
                  Your summarized content will appear here
                </Typography>
              </Box>
            ) : (
              <List>
                {paginatedSummaries.map((summary, index) => (
                  <React.Fragment key={summary._id}>
                    {index > 0 && <Divider />}
                    {/* <ListItem
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        py: 2,
                        "&:hover": {
                          bgcolor: "action.hover",
                        },
                      }}
                      onContextMenu={(e) => handleContextMenu(e, summary)}
                    >
                      <Box sx={{ width: "100%", mb: 1 }}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <Box sx={{ flex: 1 }}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Chip
                                label={summary.type || "Summary"}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                              <Chip
                                icon={<TextIcon />}
                                label={`${summary.wordCount || summary.outputText?.split(/\s+/).length || 0} words`}
                                size="small"
                                variant="outlined"
                              />
                              <Chip
                                icon={<CalendarIcon />}
                                label={formatDate(summary.createdAt)}
                                size="small"
                                variant="outlined"
                              />
                            </Stack>
                          </Box>
                          <Box>
                            <Tooltip title="Restore">
                              <IconButton
                                size="small"
                                onClick={() => handleRestore(summary)}
                                sx={{ color: "success.main" }}
                              >
                                <RestoreIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit">
                              <IconButton
                                size="small"
                                onClick={() => handleEdit(summary)}
                                sx={{ color: "info.main" }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedSummary(summary);
                                  setDeleteDialogOpen(true);
                                }}
                                sx={{ color: "error.main" }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </Box>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Original:
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            mb: 1,
                            pl: 1,
                            borderLeft: 2,
                            borderColor: "primary.light",
                          }}
                        >
                          {summary.originalText?.substring(0, 150)}
                          {summary.originalText?.length > 150 && "..."}
                        </Typography>

                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ display: "block", mb: 1 }}
                        >
                          Summary:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 3,
                            WebkitBoxOrient: "vertical",
                            pl: 1,
                            borderLeft: 2,
                            borderColor: "success.light",
                            fontWeight: 500,
                          }}
                        >
                          {summary.outputText?.substring(0, 200)}
                          {summary.outputText?.length > 200 && "..."}
                        </Typography>
                      </Box>
                    </ListItem> */}
                   <ListItem
  sx={{
    flexDirection: "column",
    alignItems: "flex-start",
    py: 2,
    "&:hover": {
      bgcolor: "action.hover",
    },
  }}
  onContextMenu={(e) => handleContextMenu(e, summary)}
>
  <Box sx={{ width: "100%", mb: 1 }}>
    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
      <Box sx={{ flex: 1 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          {/* ONLY KEEP THIS - Operation Type Badge */}
          <Chip
            label={summary.operationType === "paraphrase" ? "🔄 Paraphrased" : "📝 Summarized"}
            size="small"
            sx={{
              bgcolor: summary.operationType === "paraphrase" ? "#10b981" : "#6366f1",
              color: "white",
              fontWeight: 600,
            }}
          />
          
          {/* COMMENT OUT OR REMOVE THIS ENTIRE BLOCK - Sub-type Badge */}
          {/* {summary.subType && summary.subType !== "default" && (
            <Chip
              label={summary.subType === "keyword" ? "🔑 Keyword Based" : 
                     summary.subType === "percentage" ? "📊 Percentage Based" :
                     summary.subType === "custom" ? "⚙️ Custom Prompt" : 
                     summary.subType === "standard" ? "📄 Standard" : summary.subType}
              size="small"
              variant="outlined"
            />
          )} */}
          
          {/* COMMENT OUT OR REMOVE THIS ENTIRE BLOCK - Mode Badge (Paragraph/Bullet Points) */}
          {/* <Chip
            icon={summary.mode === "bullets" ? <FormatListBulletedIcon /> : <TextFieldsIcon />}
            label={summary.mode === "bullets" ? "Bullet Points" : "Paragraph"}
            size="small"
            variant="outlined"
          /> */}
          
          {/* KEEP THIS - Word Count */}
          <Chip
            icon={<TextIcon />}
            label={`${summary.wordCount || summary.outputText?.split(/\s+/).length || 0} words`}
            size="small"
            variant="outlined"
          />
          
          {/* OPTIONAL: You can keep or remove the Date chip */}
          <Chip
            icon={<CalendarIcon />}
            label={formatDate(summary.createdAt)}
            size="small"
            variant="outlined"
          />
        </Stack>
      </Box>
      
      {/* Action Buttons - KEEP THESE */}
      <Box>
        <Tooltip title="Restore">
          <IconButton
            size="small"
            onClick={() => handleRestore(summary)}
            sx={{ color: "success.main" }}
          >
            <RestoreIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Edit">
          <IconButton
            size="small"
            onClick={() => handleEdit(summary)}
            sx={{ color: "info.main" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete">
          <IconButton
            size="small"
            onClick={() => {
              setSelectedSummary(summary);
              setDeleteDialogOpen(true);
            }}
            sx={{ color: "error.main" }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>

    {/* Keep Original Text Preview */}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", mb: 1 }}
    >
      Original:
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        mb: 1,
        pl: 1,
        borderLeft: 2,
        borderColor: "primary.light",
      }}
    >
      {summary.originalText?.substring(0, 150)}
      {summary.originalText?.length > 150 && "..."}
    </Typography>

    {/* Keep Output Text Preview */}
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ display: "block", mb: 1 }}
    >
      {summary.operationType === "paraphrase" ? "Paraphrased:" : "Summary:"}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        overflow: "hidden",
        textOverflow: "ellipsis",
        display: "-webkit-box",
        WebkitLineClamp: 3,
        WebkitBoxOrient: "vertical",
        pl: 1,
        borderLeft: 2,
        borderColor: summary.operationType === "paraphrase" ? "#10b981" : "#6366f1",
        fontWeight: 500,
      }}
    >
      {summary.outputText?.substring(0, 200)}
      {summary.outputText?.length > 200 && "..."}
    </Typography>
    
    {/* OPTIONAL: You can also comment out this metadata stats block if you don't want to show reduction info */}
    {/* {summary.metadata?.wordCountReduction > 0 && (
      <Box sx={{ mt: 1 }}>
        <Typography variant="caption" color="text.secondary">
          ✂️ Reduced by {summary.metadata.wordCountReduction} words
          {summary.metadata.sliderValue > 0 && ` (${summary.metadata.sliderValue}% adjustment)`}
        </Typography>
      </Box>
    )} */}
  </Box>
</ListItem>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>

          {totalPages > 1 && (
            <Box sx={{ p: 2, borderTop: 1, borderColor: "divider" }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
                size="small"
                sx={{ display: "flex", justifyContent: "center" }}
              />
            </Box>
          )}
        </Box>
      </Drawer>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem
          onClick={() => {
            if (contextMenuSummary) {
              handleRestore(contextMenuSummary);
            }
            handleMenuClose();
          }}
        >
          <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
          Restore
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (contextMenuSummary) {
              handleEdit(contextMenuSummary);
            }
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} fontSize="small" />
          Edit
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            if (contextMenuSummary) {
              setSelectedSummary(contextMenuSummary);
              setDeleteDialogOpen(true);
            }
            handleMenuClose();
          }}
          sx={{ color: "error.main" }}
        >
          <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Summary</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Summary Text"
            type="text"
            fullWidth
            multiline
            rows={10}
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained" color="primary">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Summary</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this summary?
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SummaryHistory;